import { PlantDesignConstants } from 'plantDesign/constants/plantDesignConstants';
import { PHONE_TYPES } from 'shared/utils/PhoneNumber';
import { isEmpty } from 'shared/utils/Validation';
import { getPreferredPhoneNumberByType, getQuestionAnswers } from '../../shared/utils/FormatHelper';
import { capitalizeFirstLetter } from '../../shared/utils/Helpers';
import { formatPlantDesignSummary } from './PlantDesignSummaryFormatHelper';

const generatePlantDesignFormattedApplication = (state) => {
  const {
    application: app,
    plantDesignApplicantDetails: applicant,
    plantDesignHolderDetails: holder,
    plantDesignReferenceData: ref,
  } = state;

  const isIndividual = holder.registrationType === 'individual';
  const isOrganisation = holder.registrationType === 'organisation';

  // HACK: Redirect to the contact details page if they have old contact data from save/resume.
  if (hasOldContact(holder)) {
    window.location.replace('plant/design/holder-details');
  }
  ////////////////

  let formattedApplication = {
    applicationId: app.applicationId,
    applicationNo: app.applicationNo,
    licenceType: app.licenceType,
    applicantType: PlantDesignConstants.applicantType,
    plantTransactionType: PlantDesignConstants.transactionType,
    SnswServiceActivated: false,
    durationRequested: PlantDesignConstants.duration,
    ...(isIndividual && formattedIndividualApplicant(applicant, holder, ref)),
    ...(isOrganisation && formattedOrganisationApplicant(applicant, holder, ref)),
    questionList: formatQuestionList(),
    attachments: [],
    plantDesign: formatPlantDesignDetails(state),
    contactDetails: [formatContactDetails(applicant, holder)],
    itemCount: PlantDesignConstants.itemCount,
    employer: applicant.employer,
    jobTitle: applicant.jobTitle,
    applicationSummary: formatPlantDesignSummary(state),
  };

  return formattedApplication;
};

const formatQuestionList = (disclosures, disclosuresRefData) => {
  //todo populate these data from ref data
  return [
    {
      questionGroupDescription: 'Disclosures',
      questionAnswers: getQuestionAnswers(),
    },
  ];
};

const formatPlantDesignDetails = (state) => {
  const { plantDesign, plantDesignDrawingAndTech, plantDesignVerifier, plantDesignHolderDetails } = state;

  return {
    ...mapDesignDetails(plantDesign),
    verifierDetails: mapDesignerOrVerifierDetails(
      plantDesignVerifier.verifierDetails,
      plantDesignVerifier.uploadedDocument,
    ),
    designerDetails:
      plantDesignVerifier.isDesigner === 'yes'
        ? mapDesignerOrVerifierDetails(state.plantDesignApplicantDetails)
        : mapDesignerOrVerifierDetails(plantDesignVerifier.designerDetails),
    drawings: mapDrawings(plantDesignDrawingAndTech.drawings),
    techStandards: mapTechStandards(plantDesignDrawingAndTech.techStandards),
    engineeringPrinciples: mapEngineeringPrinciples(plantDesignDrawingAndTech.engineeringPrinciples),
    reasonToRegister: plantDesignHolderDetails.reasonToRegister,
  };
};

const mapDesignDetails = (designDetails) => {
  const { plantSpecificDetails } = designDetails;
  // HACK: Possibly this can update values from free text, but should not be a big problem at this point in time.
  for (let key in plantSpecificDetails) {
    plantSpecificDetails[key] = formatYesAndNo(plantSpecificDetails[key]);
  }

  return {
    plantType: designDetails.plantType,
    subType: designDetails.subType,
    otherSubType: designDetails.otherSubType,
    registrationType: designDetails.registrationType,
    manufacturer: designDetails.manufacturer,
    modelNameOrNumber: designDetails.modelNameOrNumber,
    yearOfDesign: designDetails.yearOfDesign,
    ...designDetails.alterationDetails,
    ...designDetails.plantSpecificDetails,
  };
};

const mapDesignerOrVerifierDetails = (details, file) => {
  return (
    details && {
      firstName: capitalizeFirstLetter(details.firstName?.trim()),
      middleName: capitalizeFirstLetter(details.middleName?.trim()),
      lastName: capitalizeFirstLetter(details.lastName?.trim()),
      businessName: details.businessName || details.employer,
      uploadedDocument: file?.map((doc) => ({
        id: doc.id,
        name: doc.name,
        size: doc.size,
        type: doc.type,
      })),
    }
  );
};

const mapDrawings = (drawings) => {
  return drawings?.map((drawing) => ({
    title: drawing.title,
    number: drawing.number,
    revisionNumber: drawing.revisionNumber,
    uploadedDocument: drawing.uploadedDocument?.map((doc) => ({
      id: doc.id,
      name: doc.name,
      size: doc.size,
      type: doc.type,
    })),
  }));
};

const mapTechStandards = (techStandards) => {
  return techStandards?.map((standard) => ({
    number: standard.number,
    yearOfPublication: standard.yearOfPublication,
  }));
};

const mapEngineeringPrinciples = (principles) => {
  return (
    principles && {
      complyWithTechStandard: formatYesAndNo(principles.complyWithTechStandard),
      engineeringPrincipleFile: principles.engineeringPrincipleFile?.map((file) => ({
        id: file.id,
        name: file.name,
        size: file.size,
        type: file.type,
      })),
    }
  );
};

const formattedIndividualApplicant = (applicantDetails, holderDetails, referenceData) => {
  let addressTypes = referenceData.data.addressType;
  let residentialAddressType = addressTypes.find((ad) => ad.description === 'Residential');
  const { contactMethod } = holderDetails;

  const holderMobileNumber = getPreferredPhoneNumberByType(
    contactMethod.primary,
    contactMethod?.secondary,
    PHONE_TYPES.MOBILE,
  );
  const holderLandlineNumber = getPreferredPhoneNumberByType(
    contactMethod.primary,
    contactMethod?.secondary,
    PHONE_TYPES.LANDLINE,
  );

  let individualApplicantDetails = {
    roles: ['Applicant'],
    id: 'applicant1',
    firstName: applicantDetails.firstName,
    middleName: applicantDetails.middleName,
    surname: applicantDetails.lastName,
    email: applicantDetails.emailAddress,
    phoneNumber: contactMethod.primary.formattedNumber,
    mobileNumber: holderMobileNumber,
    contactPart: contactMethod.secondary?.formattedNumber?.isAustralian && {
      phoneNo: contactMethod.secondary.formattedNumber,
      // This emailAddress is added only for passing the validation on the contactPart in the backend.
      emailAddress: applicantDetails.emailAddress,
    },
  };

  const addressTypeId = residentialAddressType?.code ? parseInt(residentialAddressType.code) : 40;
  const addresses = formatAddresses(
    holderDetails,
    holderDetails.residentialAddress,
    'Residential',
    addressTypeId,
    addressTypes,
  );

  let associatedIndividuals = null;

  // Applicant is rego holder
  if (holderDetails.isRegistrationHolder === 'yes') {
    individualApplicantDetails.addresses = addresses;
    individualApplicantDetails.phoneNumber = holderDetails.contactMethod.primary.formattedNumber;
    individualApplicantDetails.mobileNumber = holderMobileNumber;
    individualApplicantDetails.landlineNumber = holderLandlineNumber;
  } else {
    // Applicant is NOT rego holder
    associatedIndividuals = [
      {
        roles: ['Registration holder'],
        id: 'Registration holder',
        firstName: holderDetails.firstName,
        middleName: holderDetails.middleName,
        surname: holderDetails.lastName,
        email: holderDetails.emailAddress,
        phoneNumber: holderDetails.contactMethod.primary.formattedNumber,
        mobileNumber: holderMobileNumber,
        landlineNumber: holderLandlineNumber,
        addresses: addresses,
      },
    ];
  }

  return { individualApplicantDetails, associatedIndividuals };
};

const formattedOrganisationApplicant = (applicantDetails, holderDetails, referenceData) => {
  let addressTypes = referenceData.data.addressType;
  let contact = holderDetails.contactMethod;
  let registeredAddressType = addressTypes.find((ad) => ad.description === 'Registered');
  const residentialAddressTypeId =
    registeredAddressType && registeredAddressType.code ? parseInt(registeredAddressType.code) : 70;

  return {
    individualApplicantDetails: {
      roles: ['Applicant'],
      id: 'applicant1',
      firstName: applicantDetails.firstName,
      middleName: applicantDetails.middleName,
      surname: applicantDetails.lastName,
      email: applicantDetails.emailAddress,
    },
    organisationApplicantDetails: {
      id: 'corp0',
      acn: holderDetails?.organisation?.acn,
      abn: holderDetails?.organisation?.abn,
      arbn: holderDetails?.organisation?.arbn,
      name: holderDetails?.organisation?.organisationName,
      registeredBusinessName: holderDetails?.organisation?.selectedBusinessName,
      phoneNumber: contact.primary.formattedNumber,
      phoneNumber2: contact.secondary?.formattedNumber,
      mobileNumber: getPreferredPhoneNumberByType(contact.primary, contact.secondary, PHONE_TYPES.MOBILE),
      landlineNumber: getPreferredPhoneNumberByType(contact.primary, contact.secondary, PHONE_TYPES.LANDLINE),
      emailAddress: holderDetails.emailAddress,
      addresses: formatAddresses(
        holderDetails,
        holderDetails.businessAddress,
        'Registered',
        residentialAddressTypeId,
        addressTypes,
      ),
    },
  };
};

const formatAddresses = (holderDetails, primaryAddress, primaryAddressType, addressTypeId, addressTypes) => {
  const addresses = [];

  // Primary address
  addresses.push(formatAddress(primaryAddress, primaryAddressType, addressTypeId, true));

  // Mailing address
  const mailingAddressType = addressTypes.find((ad) => ad.description === 'Mailing');
  const mailingAddressTypeCode = mailingAddressType.code ? parseInt(mailingAddressType.code) : 10;

  const mailingAddress = holderDetails.isSameAddress ? primaryAddress : holderDetails.mailingAddress;

  addresses.push(formatAddress(mailingAddress, 'Mailing', mailingAddressTypeCode, false));

  return addresses;
};

const formatAddress = (address, addressType, addressTypeId, isPrimary) => {
  const isInternational = address?.addressType === 'international';
  let enterManually = address?.addressMode === 'manual' || isInternational;

  let manualAddress = {}; // Set this to null?
  let details = null;

  if (enterManually || isInternational) {
    manualAddress = isInternational
      ? formatInternationalAddress(address.address, isPrimary)
      : formatManualAddress(address.addressUi, isPrimary);
  } else {
    details = getAddressDetails(address.address, isPrimary);
  }

  return {
    addressType: addressType,
    addressTypeID: addressTypeId,
    enterManually,
    manualAddress,
    // lookupValue: holderDetails.address.lookupValue, // TODO!
    details,
  };
};

const getAddressDetails = (address, isPrimary) => {
  return {
    Address: {
      ...address.Address,
      isPrimary,
    },
    Status: address.Address.Status,
  };
};

const formatManualAddress = (address, isPrimary) => {
  return {
    addressLine: address.streetAddress?.value,
    suburb: address.suburb?.value,
    state: address.state?.value,
    postCode: address.postcode?.value,
    country: address.country?.value?.toUpperCase(),
    isPrimary: isPrimary && isPrimary === true ? true : null,
  };
};

const formatInternationalAddress = (address, isPrimary) => {
  const { streetAddress, province, city, zipCode, country } = address;
  return {
    addressLine: streetAddress,
    suburb: city,
    state: province,
    postCode: zipCode,
    country: country,
    isPrimary: isPrimary && isPrimary === true ? true : null,
  };
};

const formatContactDetails = (applicantDetails, holderDetails) => {
  let holder = holderDetails.isRegistrationHolder === 'no' ? holderDetails : applicantDetails;

  return {
    firstName: holder.firstName,
    middleName: holder.middleName,
    lastName: holder.lastName,
    email: applicantDetails.emailAddress, // Prefered email is always from applicant
    abnOrBusinessName: holderDetails.organisation?.organisationName,
    mobileNumber: getPreferredPhoneNumberByType(
      holderDetails.contactMethod.primary,
      holderDetails.contactMethod.secondary,
      PHONE_TYPES.MOBILE,
    ),
    landlineNumber: getPreferredPhoneNumberByType(
      holderDetails.contactMethod.primary,
      holderDetails.contactMethod.secondary,
      PHONE_TYPES.LANDLINE,
    ),
  };
};

const formatYesAndNo = (str) => {
  if (str === 'yes') return 'Yes';
  if (str === 'no') return 'No';

  return str;
};

const hasOldContact = (contactMethod) => {
  return !contactMethod?.primary && !isEmpty(contactMethod?.primaryContact);
};

export { generatePlantDesignFormattedApplication };
